import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { createPortal } from "react-dom";
import { Upload, Loader2, X, Undo2, CheckCircle2 } from "lucide-react";

const STATI = {
  nuovo: ["Nuovo", "bg-emerald-100 text-emerald-700"],
  gia_presente: ["Già presente", "bg-slate-200 text-slate-700"],
  da_controllare: ["Da controllare", "bg-amber-100 text-amber-800"],
};
const eur = (n) => (n != null ? `€ ${Number(n).toLocaleString("it-IT")}` : "—");

function Anteprima({ imp }) {
  const conta = (s) => imp.righe.filter((r) => r.stato === s).length;
  return (
    <>
      <div className="flex gap-2 flex-wrap text-xs mb-3" data-testid="import-conteggi">
        {Object.entries(STATI).map(([k, [l, c]]) => <span key={k} className={`px-2.5 py-1 rounded-full font-semibold ${c}`}>{l}: {conta(k)}</span>)}
      </div>
      {!imp.jennifer_trovata && <p className="text-xs text-red-600 mb-2" data-testid="import-no-jennifer">Utente "Jennifer Bezzi" non trovato: le righe con agente non riconosciuto verranno saltate.</p>}
      <div className="overflow-auto max-h-[50vh] border border-[var(--border)] rounded-xl">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="bg-[#F7F7F8] sticky top-0 text-[10px] uppercase text-[var(--muted)]">
            <tr>{["Riga", "Stato", "Codice", "Indirizzo", "Comune", "Tipologia", "Prezzo", "Mq", "Agente", "Venditore", "Incarico", "Note"].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {imp.righe.map((r) => {
              const d = r.dati;
              return (
                <tr key={r.riga} data-testid={`import-riga-${r.riga}`}>
                  <td className="p-2 mono">{r.riga}</td>
                  <td className="p-2"><span data-testid={`import-stato-${r.riga}`} className={`px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${STATI[r.stato][1]}`}>{STATI[r.stato][0]}</span></td>
                  <td className="p-2 mono">{d.codice_gestim || "—"}</td>
                  <td className="p-2">{d.indirizzo || "—"}</td>
                  <td className="p-2">{d.comune || "—"}{d.provincia ? ` (${d.provincia})` : ""}</td>
                  <td className="p-2">{d.tipologia || "—"}</td>
                  <td className="p-2 mono whitespace-nowrap">{eur(d.prezzo)}</td>
                  <td className="p-2 mono">{d.mq ?? "—"}</td>
                  <td className="p-2">{r.agente_nome || "—"}{r.agente_da_assegnare && <div className="text-[10px] font-bold text-amber-700">Agente da assegnare</div>}</td>
                  <td className="p-2">{d.proprietario_nome || "—"}<div className="text-[10px] text-[var(--muted)]">{d.proprietario_telefono || ""}</div></td>
                  <td className="p-2 whitespace-nowrap">{d.data_inizio_incarico || "—"} → {d.scadenza_incarico || "—"}</td>
                  <td className="p-2 text-[10px] text-amber-800">{r.avvisi.join(" · ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RigheProblemi({ righe }) {
  if (!righe?.length) return <p className="text-xs text-[var(--muted)] mt-2" data-testid="import-problemi-vuoto">Nessuna riga saltata o da controllare.</p>;
  return (
    <div className="mt-3 border border-[var(--border)] rounded-xl overflow-auto max-h-[40vh]" data-testid="import-problemi">
      <table className="w-full text-xs min-w-[560px]">
        <thead className="bg-[#F7F7F8] text-[10px] uppercase text-[var(--muted)]"><tr><th className="p-2 text-left">Riga</th><th className="p-2 text-left">Esito</th><th className="p-2 text-left">Immobile</th><th className="p-2 text-left">Motivo</th></tr></thead>
        <tbody className="divide-y divide-[var(--border)]">
          {righe.map((r) => (
            <tr key={r.riga} data-testid={`import-problema-${r.riga}`}>
              <td className="p-2 mono">{r.riga}</td>
              <td className={`p-2 font-semibold whitespace-nowrap ${r.esito === "Saltata" ? "text-red-700" : "text-amber-700"}`}>{r.esito}</td>
              <td className="p-2">{r.descrizione}</td>
              <td className="p-2">{r.motivo}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Storico({ list, onAnnulla }) {
  const [aperto, setAperto] = useState(null);
  const [righe, setRighe] = useState({});
  const apri = (id) => {
    setAperto(aperto === id ? null : id);
    if (!righe[id]) api.get(`/immobili/import/${id}/dettaglio`).then((r) => setRighe((p) => ({ ...p, [id]: r.data.righe }))).catch(() => {});
  };
  if (!list.length) return null;
  return (
    <div className="mt-4 pt-4 border-t border-[var(--border)]" data-testid="import-storico">
      <div className="text-[11px] font-bold uppercase text-[var(--muted)] mb-2">Ultime importazioni</div>
      {list.map((i) => {
        const annullabile = i.stato === "confermato" && new Date(i.annullabile_fino) > new Date();
        return (
          <div key={i.id} className="py-1.5 text-xs" data-testid={`import-storico-${i.id}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <button data-testid={`import-storico-apri-${i.id}`} onClick={() => apri(i.id)} className="flex-1 min-w-0 text-left hover:underline">{aperto === i.id ? "▾" : "▸"} {new Date(i.confermato_at).toLocaleString("it-IT")} · {i.file} · {i.by} — importati {i.esiti?.importati}, saltati {i.esiti?.saltati}, da controllare {i.esiti?.da_controllare}{i.stato === "annullato" && <b className="text-red-600"> · ANNULLATA</b>}</button>
            {annullabile && <button data-testid={`import-annulla-${i.id}`} onClick={() => onAnnulla(i)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-300 text-red-700 font-semibold"><Undo2 size={13} /> Annulla importazione</button>}
          </div>
          {aperto === i.id && (righe[i.id] ? <RigheProblemi righe={righe[i.id]} /> : <p className="text-xs text-[var(--muted)] mt-2">Caricamento…</p>)}
          </div>
        );
      })}
    </div>
  );
}

export default function ImportImmobiliModal({ onClose, onDone }) {
  const [imp, setImp] = useState(null);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);
  const [storico, setStorico] = useState([]);
  const loadStorico = () => api.get("/immobili/import").then((r) => setStorico(r.data)).catch(() => {});
  useEffect(() => { loadStorico(); }, []);
  const carica = async (file) => {
    if (!file) return;
    setBusy(true); setEsito(null);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/immobili/import/anteprima", fd); setImp(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const conferma = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/immobili/import/${imp.id}/conferma`); setEsito(data); setImp(null); loadStorico(); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const annulla = async (i) => {
    if (!window.confirm(`Sei sicura? Verranno eliminati i ${i.esiti?.importati} immobili importati e i venditori creati da questa importazione.`)) return;
    try { const { data } = await api.post(`/immobili/import/${i.id}/annulla`); toast.success(`Importazione annullata: ${data.immobili_rimossi} immobili e ${data.venditori_rimossi} venditori rimossi`); setEsito(null); loadStorico(); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" data-testid="import-immobili-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-6xl card-immo p-5 max-h-[92vh] overflow-y-auto">
        <button data-testid="import-close" onClick={onClose} className="absolute right-4 top-4 text-slate-500"><X size={18} /></button>
        <h3 className="font-display text-lg font-bold mb-1">Importa immobili da file</h3>
        <p className="text-xs text-[var(--muted)] mb-4">CSV con separatore ";" (UTF-8) e le colonne previste. Prima vedi l'anteprima: nulla viene salvato finché non confermi. Le righe "Già presente" non vengono importate né sovrascritte.</p>
        {!imp && (
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold cursor-pointer">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Scegli file CSV
            <input data-testid="import-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { carica(e.target.files[0]); e.target.value = ""; }} />
          </label>
        )}
        {esito && (
          <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm" data-testid="import-esito">
            <div className="flex items-center gap-2 font-bold text-emerald-800"><CheckCircle2 size={16} /> Importazione completata</div>
            <div className="mt-1">Importati: <b data-testid="import-esito-importati">{esito.importati}</b> · Saltati: <b data-testid="import-esito-saltati">{esito.saltati}</b> · Da controllare: <b data-testid="import-esito-controllare">{esito.da_controllare}</b></div>
            <div className="text-xs text-[var(--muted)] mt-1">Puoi annullare l'intera importazione fino al {new Date(esito.annullabile_fino).toLocaleString("it-IT")}.</div>
            <div className="mt-3 text-[11px] font-bold uppercase text-[var(--muted)]">Righe saltate e da controllare</div>
            <RigheProblemi righe={esito.righe} />
          </div>
        )}
        {imp && (
          <>
            <Anteprima imp={imp} />
            <div className="flex gap-2 mt-4 flex-wrap">
              <button data-testid="import-conferma" onClick={conferma} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] text-black text-sm font-semibold disabled:opacity-50">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Conferma importazione
              </button>
              <button data-testid="import-cambia-file" onClick={() => setImp(null)} className="px-4 py-2.5 rounded-xl border border-[var(--border)] text-sm">Annulla e scegli un altro file</button>
            </div>
          </>
        )}
        <Storico list={storico} onAnnulla={annulla} />
      </div>
    </div>, document.body);
}
